import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Circle } from 'lucide-react';
import { AddDOAModal } from '@/components/regulatory/AddDOAModal';
import { CTMS_TASKS } from '@/lib/ctms/taskCatalog';

interface Props {
  croTrialId: string;
  trialSiteId: string;
  onInstall: () => void;
  onEnroll: () => void;
}

/** Create -> Install -> Staff -> Patients, in order, for one site trial. */
export function TrialSetupChecklist({ croTrialId, trialSiteId, onInstall, onEnroll }: Props) {
  const [doaOpen, setDoaOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['trial-setup-status', croTrialId, trialSiteId],
    queryFn: async () => {
      const db = supabase as any;
      const { data: ct } = await db.from('cro_trials').select('id, trial_id').eq('id', croTrialId).maybeSingle();
      const registryId: string | null = ct?.trial_id ?? null;
      const [bundles, doa, pts] = await Promise.all([
        db.from('compliance_audit_trail').select('id', { count: 'exact', head: true })
          .eq('action', 'trial_site_install').eq('entity_id', trialSiteId).eq('metadata->>trial_id', croTrialId),
        registryId
          ? db.from('delegation_of_authority').select('id', { count: 'exact', head: true })
              .eq('trial_site_id', trialSiteId).eq('trial_id', registryId)
          : Promise.resolve({ count: 0 }),
        db.from('patient_trial_site_assignments').select('id', { count: 'exact', head: true })
          .eq('trial_site_id', trialSiteId).eq('cro_trial_id', croTrialId).eq('is_active_enrollment', true),
      ]);
      return {
        registryId,
        installed: (bundles.count ?? 0) > 0,
        delegated: (doa.count ?? 0) > 0,
        enrolled: pts.count ?? 0,
      };
    },
  });

  const installed = !!data?.installed;
  const steps = [
    { label: 'Trial added', done: true },
    { label: 'Installed (visits and forms)', done: installed, action: !installed && { text: 'Install', fn: onInstall } },
    { label: 'Staff delegated for this trial', done: !!data?.delegated,
      action: !data?.delegated && { text: 'Delegate staff', fn: () => setDoaOpen(true) } },
    { label: data?.enrolled ? `Patients enrolled (${data.enrolled})` : 'Patients enrolled', done: !!data?.enrolled,
      action: { text: 'Enroll patient', fn: onEnroll, disabled: !installed } },
  ];

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <p className="text-sm font-medium">Set up this trial</p>
      {steps.map((s, i) => (
        <div key={i} className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm">
            {s.done ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
            <span>{i + 1}. {s.label}</span>
          </div>
          {s.action && (
            <Button size="sm" variant="outline" disabled={(s.action as any).disabled} onClick={s.action.fn}>
              {s.action.text}
            </Button>
          )}
        </div>
      ))}
      {!installed && <p className="text-xs text-muted-foreground">Install the trial first. Then you can enroll patients.</p>}
      <AddDOAModal
        isOpen={doaOpen}
        onClose={() => setDoaOpen(false)}
        siteId={trialSiteId}
        taskCategories={CTMS_TASKS.map((t) => t.key)}
        defaultTrialId={data?.registryId}
      />
    </div>
  );
}
